// PATH: src/components/modules/devtools/panels/ReleaseSyncPanel.js

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import ReleaseSyncFileHistoryDialog from "../components/ReleaseSyncFileHistoryDialog";
import {
  DEFAULT_DIRECTION,
  fetchReleaseSyncHistory,
  fetchReleaseSyncProjects,
  fetchReleaseSyncScan,
} from "../utils/releaseSyncApi";

const TEST_ENVIRONMENTS = new Set(["test", "presandbox"]);
const PRODUCTION_ENVIRONMENTS = new Set(["production", "sandbox"]);
const CONFLICT_PROVENANCE = "DIVERGED/CONFLICT";
const SOURCE_DIFFERENCE_STATES = new Set(["SOURCE_AHEAD", "SOURCE_NEW", "SOURCE_DELETE"]);

const normalise = (value) => String(value || "").trim().toLowerCase();
const shortHash = (value) => (value ? `${String(value).slice(0, 12)}…` : "—");

const projectLabel = (project) => {
  if (!project) return "Unknown project";
  const label = project.label || project.project_id || "Project";
  const environment = project.environment_type ? ` • ${project.environment_type}` : "";
  return `${label}${environment}`;
};

const isResolved = (project) =>
  Boolean(project?.project_id) && normalise(project?.project_identity_status) === "resolved";

const isManualSelectable = (project) => Boolean(project?.project_id);

const isTestLike = (project) => TEST_ENVIRONMENTS.has(normalise(project?.environment_type));
const isProductionLike = (project) =>
  PRODUCTION_ENVIRONMENTS.has(normalise(project?.environment_type));

const getErrorText = (error) => error?.message || String(error || "Unknown error");

const defaultReconciliation = (asset) => ({
  ...asset,
  provenance_status: asset.state === "ALIGNED" ? "ALIGNED" : "UNKNOWN",
  confidence: asset.state === "ALIGNED" ? "ALIGNED_HISTORY_UNKNOWN" : "UNKNOWN_PROVENANCE",
  source_history: [],
  target_history: [],
  evidence: {},
  first_subsequent_source_changes: [],
  subsequent_source_changes: [],
  subsequent_full_change_groups: [],
  source_pending_changes: [],
  source_pending_full_change_groups: [],
  source_current_change: null,
  source_current_full_change: null,
  source_current_attribution_status: "UNKNOWN_SOURCE_HISTORY",
  source_evidenced_changes: [],
  source_evidenced_full_change_groups: [],
  target_inferred_source_position: null,
});

const compactWorkspaceHistoryAsset = (asset) => {
  const sourceHistory = asset?.source_history || [];
  const latestSourceChange = sourceHistory.length
    ? sourceHistory[sourceHistory.length - 1]?.change || null
    : null;
  const workspaceAsset = { ...(asset || {}) };
  delete workspaceAsset.source_history;
  delete workspaceAsset.target_history;
  delete workspaceAsset.target_exact_source_history_matches;
  delete workspaceAsset.target_current_applied_history_matches;
  delete workspaceAsset.source_current_applied_history_matches;
  return {
    ...workspaceAsset,
    source_history: [],
    target_history: [],
    workspace_latest_source_change: latestSourceChange,
  };
};

const getPrimaryChange = (asset) => {
  const pendingSourceChanges = asset.source_pending_changes || [];
  if (pendingSourceChanges.length) return pendingSourceChanges[0];
  const firstSubsequent = asset.first_subsequent_source_changes || [];
  if (firstSubsequent.length) return firstSubsequent[0];
  if (asset.source_current_change) return asset.source_current_change;
  const auditChanges = asset.source_audit_changes || [];
  if (auditChanges.length) return auditChanges[auditChanges.length - 1];
  const evidenced = asset.source_evidenced_changes || [];
  if (evidenced.length) return evidenced[evidenced.length - 1];
  if (asset.workspace_latest_source_change) return asset.workspace_latest_source_change;
  const sourceHistory = asset.source_history || [];
  if (sourceHistory.length) return sourceHistory[sourceHistory.length - 1]?.change || null;
  return null;
};

const getGroup = (asset, grouping) => {
  if (grouping === "file") {
    return { key: asset.path, label: asset.path };
  }

  const change = getPrimaryChange(asset);
  if (grouping === "change") {
    if (!change?.change_id) {
      return { key: "change-unmapped", label: "Unmapped / no Change evidence" };
    }
    return {
      key: `change-${change.change_id}`,
      label: `Change #${change.change_id} — ${change.title || "Untitled"}`,
    };
  }

  const fullChange =
    change?.full_change ||
    asset.source_current_full_change ||
    asset.source_pending_full_change_groups?.[0]?.full_change ||
    asset.subsequent_full_change_groups?.[0]?.full_change;
  if (!fullChange?.full_change_id) {
    return { key: "full-change-unmapped", label: "Unmapped / no Full Change evidence" };
  }
  return {
    key: `full-change-${fullChange.full_change_id}`,
    label: `Full Change #${fullChange.full_change_id} — ${fullChange.title || "Untitled"}`,
  };
};

const hasHistoryEvidence = (asset) => {
  const evidence = asset.evidence || {};
  return Boolean(
    asset.target_inferred_source_position ||
      (asset.source_history || []).length ||
      (asset.target_history || []).length ||
      evidence.source_applied_save_count ||
      evidence.target_applied_save_count
  );
};

const matchesView = (asset, view) => {
  if (view === "pending") {
    return ["SOURCE_AHEAD", "SOURCE_NEW", "SOURCE_DELETE"].includes(asset.state);
  }
  if (view === "conflict") return asset.state === "CONFLICT";
  if (view === "untracked") {
    return ["TARGET_AHEAD", "TARGET_ONLY", "UNTRACKED_UNKNOWN", "UNREADABLE"].includes(asset.state);
  }
  if (view === "synced") return ["ALIGNED", "BOTH_CHANGED_SAME"].includes(asset.state);
  if (view === "history") return hasHistoryEvidence(asset);
  return true;
};

const stateChipColor = (state) => {
  if (state === "ALIGNED") return "success";
  if (["SOURCE_AHEAD", "SOURCE_NEW"].includes(state)) return "primary";
  if (["SOURCE_DELETE", "TARGET_AHEAD", "TARGET_ONLY", "BOTH_CHANGED_SAME"].includes(state)) return "warning";
  if (state === "CONFLICT") return "error";
  return "default";
};

const getAllSourceChanges = (asset) => {
  const changes = new Map();
  const addChange = (change) => {
    if (!change?.change_id) return;
    changes.set(String(change.change_id), change);
  };

  (asset.source_audit_changes || []).forEach(addChange);
  (asset.source_evidenced_changes || []).forEach(addChange);
  (asset.source_recorded_membership_changes || []).forEach(addChange);
  (asset.source_change_log_only_changes || []).forEach(addChange);
  (asset.source_pending_changes || []).forEach(addChange);
  (asset.subsequent_source_changes || []).forEach(addChange);
  (asset.first_subsequent_source_changes || []).forEach(addChange);
  addChange(asset.source_current_change);
  addChange(asset.workspace_latest_source_change);
  (asset.source_history || []).forEach((item) => addChange(item?.change));
  return Array.from(changes.values());
};

const getOutstandingReleaseChanges = (asset) => asset.source_outstanding_release_changes || [];

const getAllFullChanges = (asset, changes = getAllSourceChanges(asset)) => {
  const fullChanges = new Map();
  const addFullChange = (fullChange) => {
    if (!fullChange?.full_change_id) return;
    fullChanges.set(String(fullChange.full_change_id), fullChange);
  };

  changes.forEach((change) => addFullChange(change?.full_change));
  addFullChange(asset.source_current_full_change);
  (asset.source_audit_full_change_groups || []).forEach((group) => addFullChange(group?.full_change));
  (asset.source_evidenced_full_change_groups || []).forEach((group) => addFullChange(group?.full_change));
  (asset.source_pending_full_change_groups || []).forEach((group) => addFullChange(group?.full_change));
  (asset.subsequent_full_change_groups || []).forEach((group) => addFullChange(group?.full_change));
  return Array.from(fullChanges.values());
};

const auditEvidenceTier = (asset) => {
  const tiers = asset?.source_audit_evidence_tiers || {};
  if ((tiers.exact_or_applied || []).length) return "EXACT_OR_APPLIED";
  if ((tiers.recorded_change_membership || []).length) return "RECORDED_CHANGE_MEMBERSHIP";
  if ((tiers.change_log_only || []).length) return "CHANGE_LOG_ONLY";
  return "UNKNOWN";
};

const evidenceBreakdown = (assets) => {
  const counts = { EXACT_OR_APPLIED: 0, RECORDED_CHANGE_MEMBERSHIP: 0, CHANGE_LOG_ONLY: 0, UNKNOWN: 0 };
  (assets || []).forEach((asset) => { counts[auditEvidenceTier(asset)] += 1; });
  return counts;
};

const stateBreakdown = (assets) => {
  const counts = new Map();
  (assets || []).forEach((asset) => {
    const state = asset?.state || "UNKNOWN";
    counts.set(state, (counts.get(state) || 0) + 1);
  });
  return ["SOURCE_NEW", "SOURCE_AHEAD", "SOURCE_DELETE", "CONFLICT"]
    .filter((state) => counts.get(state))
    .map((state) => `${counts.get(state)} ${state}`)
    .join(" • ");
};

const buildReleaseDifferenceSummary = (assets, releaseDeltaAudit = null) => {
  const sourceAssets = (assets || []).filter((asset) => SOURCE_DIFFERENCE_STATES.has(asset?.state));
  const fullChanges = new Map();
  const changes = new Map();
  const attributedPaths = new Set();

  const auditFullChanges = releaseDeltaAudit?.full_changes || [];
  auditFullChanges.forEach((fullChange) => {
    if (!fullChange?.full_change_id) return;
    fullChanges.set(String(fullChange.full_change_id), { fullChange, assets: new Map(), changes: new Map() });
  });

  sourceAssets.forEach((asset) => {
    const releaseChanges = getOutstandingReleaseChanges(asset);
    releaseChanges.forEach((change) => {
      if (!change?.change_id) return;
      const key = String(change.change_id);
      if (!changes.has(key)) changes.set(key, { change, assets: new Map() });
      changes.get(key).assets.set(asset.path, asset);
      attributedPaths.add(asset.path);

      const fullChange = change.full_change;
      if (fullChange?.full_change_id) {
        const fcKey = String(fullChange.full_change_id);
        if (!fullChanges.has(fcKey)) fullChanges.set(fcKey, { fullChange, assets: new Map(), changes: new Map() });
        fullChanges.get(fcKey).assets.set(asset.path, asset);
        fullChanges.get(fcKey).changes.set(key, change);
      }
    });
  });

  const normaliseId = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : String(value || "");
  };
  const sortById = (left, right, field) => {
    const leftValue = normaliseId(left[field]);
    const rightValue = normaliseId(right[field]);
    if (typeof leftValue === "number" && typeof rightValue === "number") return rightValue - leftValue;
    return String(rightValue).localeCompare(String(leftValue));
  };

  const auditCodeTotal = releaseDeltaAudit?.source_code_difference_total;
  const auditDocTotal = releaseDeltaAudit?.source_document_difference_total;
  return {
    sourceDifferenceFiles: Number.isFinite(auditCodeTotal) ? auditCodeTotal : sourceAssets.filter((a) => a.asset_type !== "DOCUMENT").length,
    sourceDocumentDifferences: Number.isFinite(auditDocTotal) ? auditDocTotal : sourceAssets.filter((a) => a.asset_type === "DOCUMENT").length,
    sourceNewCode: releaseDeltaAudit?.source_new_code_total ?? 0,
    sourceModifiedCode: releaseDeltaAudit?.source_modified_code_total ?? 0,
    sourceDeletedCode: releaseDeltaAudit?.source_deleted_code_total ?? 0,
    sourceNewDocuments: releaseDeltaAudit?.source_new_document_total ?? 0,
    sourceModifiedDocuments: releaseDeltaAudit?.source_modified_document_total ?? 0,
    sourceDeletedDocuments: releaseDeltaAudit?.source_deleted_document_total ?? 0,
    codeDifferences: releaseDeltaAudit?.code_differences || [],
    documentDifferences: releaseDeltaAudit?.document_differences || [],
    documentedChangeEntries: releaseDeltaAudit?.documented_change_entries || [],
    historicalContributorChanges: releaseDeltaAudit?.historical_contributor_change_total || 0,
    standaloneChanges: releaseDeltaAudit?.standalone_changes || [],
    standaloneChangeTotal: releaseDeltaAudit?.standalone_change_total || 0,
    attributedFiles: attributedPaths.size,
    unmappedFiles: sourceAssets.filter((asset) => getOutstandingReleaseChanges(asset).length === 0),
    fullChanges: Array.from(fullChanges.values())
      .map((entry) => ({
        ...entry.fullChange,
        assets: Array.from(entry.assets.values()).sort((a, b) => String(a.path).localeCompare(String(b.path))),
        changes: Array.from(entry.changes.values()).sort((a, b) => sortById(a, b, "change_id")),
      }))
      .sort((a, b) => sortById(a, b, "full_change_id")),
    changes: Array.from(changes.values())
      .map((entry) => ({
        ...entry.change,
        assets: Array.from(entry.assets.values()).sort((a, b) => String(a.path).localeCompare(String(b.path))),
      }))
      .sort((a, b) => sortById(a, b, "change_id")),
  };
};

const SummaryAssetRow = ({ asset, onOpen }) => {
  const primaryChange = getPrimaryChange(asset);
  return (
    <Paper variant="outlined" sx={{ p: 0.9 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
            {asset.path}
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Chip size="small" color={stateChipColor(asset.state)} label={asset.state || "UNKNOWN"} />
            {primaryChange?.change_id ? (
              <Chip size="small" variant="outlined" label={`Change #${primaryChange.change_id}`} />
            ) : null}
            <Chip size="small" variant="outlined" label={auditEvidenceTier(asset)} />
          </Stack>
        </Box>
        <Button size="small" variant="outlined" onClick={() => onOpen(asset)} sx={{ whiteSpace: "nowrap" }}>
          History / diff
        </Button>
      </Stack>
    </Paper>
  );
};

const ExpandableSummaryBlock = ({ title, helper, assets, children, onOpenAsset }) => (
  <Box
    component="details"
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      overflow: "hidden",
      "&[open] > summary": { borderBottom: "1px solid", borderColor: "divider" },
    }}
  >
    <Box
      component="summary"
      sx={{ cursor: "pointer", p: 1.1, listStylePosition: "inside", backgroundColor: "background.paper" }}
    >
      <Typography component="span" variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
        {title}
      </Typography>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        {assets.length} file{assets.length === 1 ? "" : "s"}{helper ? ` • ${helper}` : ""}
      </Typography>
    </Box>
    <Stack spacing={0.75} sx={{ p: 1 }}>
      {children}
      {assets.map((asset) => (
        <SummaryAssetRow key={asset.path} asset={asset} onOpen={onOpenAsset} />
      ))}
    </Stack>
  </Box>
);

const ReleaseDifferenceSummary = ({ data, onOpenAsset }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="subtitle1" fontWeight={700}>Release difference summary</Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
      Current target versus current source release audit. The primary Full Change/Change list is constrained by source documentation/change-history that is absent from the target, then linked to the current code/document differences. Historical contributors that merely touched a file are kept separate and are not treated as outstanding release work.
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
        gap: 1,
        mb: 1.5,
      }}
    >
      <SummaryCard label="Outstanding Full Changes" count={data.fullChanges.length} helper="Source doc/change-history delta" />
      <SummaryCard label="Outstanding DEV IDE Changes" count={data.changes.length} helper="Full Change-linked + standalone" />
      <SummaryCard label="Code differences" count={data.sourceDifferenceFiles} helper={`${data.sourceNewCode} new • ${data.sourceModifiedCode} modified${data.sourceDeletedCode ? ` • ${data.sourceDeletedCode} deleted` : ""}`} />
      <SummaryCard label="Document differences" count={data.sourceDocumentDifferences} helper={`${data.sourceNewDocuments} new • ${data.sourceModifiedDocuments} modified${data.sourceDeletedDocuments ? ` • ${data.sourceDeletedDocuments} deleted` : ""}`} />
    </Box>

    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1, mb: 1.5 }}>
      <SummaryCard label="Files linked to outstanding Changes" count={data.attributedFiles} helper="Current differences with release-delta Change evidence" />
      <SummaryCard label="Unresolved file attribution" count={data.unmappedFiles.length} helper="Difference known; exact Change linkage unresolved" />
      <SummaryCard label="Documented change entries" count={data.documentedChangeEntries.length} helper="Source change-history headings absent from target" />
      <SummaryCard label="Standalone outstanding Changes" count={data.standaloneChangeTotal} helper="No Full Change mapping" />
    </Box>

    <Alert severity="info" sx={{ mb: 1.5 }}>
      Outstanding release work is based on what exists in the current source but not the current target. Full Change-linked and standalone DEV IDE Changes are evaluated independently. Standalone Changes require exact/applied current-difference evidence or explicit source-only Change History evidence; old file membership alone is not enough.
    </Alert>

    <Stack spacing={1.5}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Full Changes</Typography>
        <Stack spacing={0.75}>
          {data.fullChanges.length ? data.fullChanges.map((fullChange) => (
            <ExpandableSummaryBlock
              key={`summary-full-${fullChange.full_change_id}`}
              title={`Full Change #${fullChange.full_change_id} — ${fullChange.title || "Untitled"}`}
              helper={`${fullChange.changes.length} Change${fullChange.changes.length === 1 ? "" : "s"}${stateBreakdown(fullChange.assets) ? ` • ${stateBreakdown(fullChange.assets)}` : ""}`}
              assets={fullChange.assets}
              onOpenAsset={onOpenAsset}
            >
              {fullChange.changes.length ? (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {fullChange.changes.map((change) => (
                    <Chip
                      key={`full-${fullChange.full_change_id}-change-${change.change_id}`}
                      size="small"
                      variant="outlined"
                      label={`Change #${change.change_id} — ${change.title || "Untitled"}`}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">No directly linked Change row was available for this Full Change evidence.</Typography>
              )}
            </ExpandableSummaryBlock>
          )) : (
            <Typography variant="body2" color="text.secondary">No Full Change evidence is linked to the current source differences.</Typography>
          )}
        </Stack>
      </Box>

      {data.standaloneChanges.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Standalone outstanding Changes</Typography>
          <Stack spacing={0.75}>
            {data.standaloneChanges.map((change) => {
              const mapped = data.changes.find((item) => String(item.change_id) === String(change.change_id));
              const assets = mapped?.assets || [];
              return (
                <ExpandableSummaryBlock
                  key={`summary-standalone-${change.change_id}`}
                  title={`Change #${change.change_id} — ${change.title || "Untitled"}`}
                  helper={`${change.release_delta_evidence === "SOURCE_DOCUMENT_CHANGE_HISTORY" ? "Source-only Change History" : "Exact/applied current-difference evidence"}${stateBreakdown(assets) ? ` • ${stateBreakdown(assets)}` : ""}`}
                  assets={assets}
                  onOpenAsset={onOpenAsset}
                >
                  {!assets.length ? (
                    <Typography variant="caption" color="text.secondary">
                      This standalone Change is present in source-only Change History but has no surviving per-file save evidence linking an exact current-difference path.
                    </Typography>
                  ) : null}
                </ExpandableSummaryBlock>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>DEV IDE Changes</Typography>
        <Stack spacing={0.75}>
          {data.changes.length ? data.changes.map((change) => (
            <ExpandableSummaryBlock
              key={`summary-change-${change.change_id}`}
              title={`Change #${change.change_id} — ${change.title || "Untitled"}`}
              helper={`${change.full_change?.full_change_id ? `Full Change #${change.full_change.full_change_id} • ` : "Standalone • "}${stateBreakdown(change.assets)}`}
              assets={change.assets}
              onOpenAsset={onOpenAsset}
            />
          )) : (
            <Typography variant="body2" color="text.secondary">No DEV IDE Change evidence is linked to the current source differences.</Typography>
          )}
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Current code delta</Typography>
        <Box component="details" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Box component="summary" sx={{ cursor: "pointer", p: 1.1 }}>
            <Typography component="span" variant="body2" fontWeight={700}>All current code differences</Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {data.codeDifferences.length} files
            </Typography>
          </Box>
          <Stack spacing={0.5} sx={{ p: 1, pt: 0 }}>
            {data.codeDifferences.map((item) => (
              <Paper key={`audit-code-${item.path}`} variant="outlined" sx={{ p: 0.75 }}>
                <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{item.path}</Typography>
                <Chip size="small" color={stateChipColor(item.state)} label={item.state} sx={{ mt: 0.5 }} />
              </Paper>
            ))}
          </Stack>
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Current document delta</Typography>
        <Box component="details" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Box component="summary" sx={{ cursor: "pointer", p: 1.1 }}>
            <Typography component="span" variant="body2" fontWeight={700}>All changed document objects</Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {data.documentDifferences.length} documents
            </Typography>
          </Box>
          <Stack spacing={0.5} sx={{ p: 1, pt: 0 }}>
            {data.documentDifferences.map((item) => (
              <Paper key={`audit-doc-${item.path}`} variant="outlined" sx={{ p: 0.75 }}>
                <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{item.path}</Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <Chip size="small" color={stateChipColor(item.state)} label={item.state} />
                  {(item.full_change_identifiers || []).map((value) => (
                    <Chip key={`${item.path}-${value}`} size="small" variant="outlined" label={`FC${value}`} />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Box>

      {data.documentedChangeEntries.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Documented source-only change entries</Typography>
          <Stack spacing={0.5}>
            {data.documentedChangeEntries.map((entry, index) => (
              <Paper key={`documented-change-${index}`} variant="outlined" sx={{ p: 0.9 }}>
                <Typography variant="body2" fontWeight={700}>{entry.heading}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                  {entry.document_path}{entry.full_change_identifiers?.length ? ` • ${entry.full_change_identifiers.map((value) => `FC${value}`).join(", ")}` : ""}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      {data.unmappedFiles.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Unmapped / no Change evidence</Typography>
          <ExpandableSummaryBlock
            title="Source differences with no Change attribution"
            helper={stateBreakdown(data.unmappedFiles)}
            assets={data.unmappedFiles}
            onOpenAsset={onOpenAsset}
          />
        </Box>
      ) : null}
    </Stack>
  </Paper>
);

const SummaryCard = ({ label, count, helper }) => (
  <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
    <Typography variant="h6" component="div">{count}</Typography>
    <Typography variant="body2" fontWeight={700}>{label}</Typography>
    {helper ? (
      <Typography variant="caption" color="text.secondary">{helper}</Typography>
    ) : null}
  </Paper>
);

const AssetRow = ({ asset, onOpen }) => {
  const primaryChange = getPrimaryChange(asset);
  const inferred = asset.target_inferred_source_position;
  const targetDrift = ["TARGET_AHEAD", "TARGET_ONLY"].includes(asset.state);

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-start" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
              {asset.path}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {asset.asset_type || "OTHER"} • source {shortHash(asset.source_sha256)} • target {" "}
              {shortHash(asset.target_sha256)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" color={stateChipColor(asset.state)} label={asset.state || "UNKNOWN"} />
            {asset.provenance_status === CONFLICT_PROVENANCE ? (
              <Chip size="small" variant="outlined" color="error" label="History: diverged" />
            ) : null}
            {asset.parity_excluded ? (
              <Chip size="small" variant="outlined" label="Explicit parity exclusion" />
            ) : null}
            {asset.source_current_change?.change_id ? (
              <Chip size="small" variant="outlined" color="primary" label={`Source Change #${asset.source_current_change.change_id}`} />
            ) : null}
            <Chip size="small" variant="outlined" label={asset.confidence || "UNKNOWN_PROVENANCE"} />
          </Stack>
        </Stack>

        {targetDrift ? (
          <Alert severity="warning" sx={{ py: 0 }}>
            Production drift is determined from current hashes versus the trusted baseline, not from Change history.
            {asset.target_drift_first_observed_at
              ? ` First observed by Release Sync at ${new Date(asset.target_drift_first_observed_at).toLocaleString()}; exact modification time is unknown.`
              : ""}
          </Alert>
        ) : null}
        {asset.parity_excluded && asset.parity_exclusion_reason ? (
          <Typography variant="caption" color="text.secondary">
            Audited parity exclusion: {asset.parity_exclusion_reason}
          </Typography>
        ) : null}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Typography variant="caption" color="text.secondary">
            {inferred
              ? `Target position inferred at ${inferred.boundary || "historical boundary"}; this is not deployment proof.`
              : asset.source_current_change?.change_id
                ? `Current source hash attributed to Change #${asset.source_current_change.change_id} ${asset.source_current_change.title || ""}. Target has no exact historical source position.`
                : primaryChange?.change_id
                  ? `Change evidence: #${primaryChange.change_id} ${primaryChange.title || ""}`
                  : "No exact source or target historical Change position established."}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => onOpen(asset)}>
            View history / diff
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const ReleaseSyncPanel = ({
  selectedProjectId: sharedSelectedProjectId,
  onSelectedProjectChange,
} = {}) => {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");
  const [localSourceProjectId, setLocalSourceProjectId] = useState("");
  const sourceProjectId =
    typeof sharedSelectedProjectId === "string" ? sharedSelectedProjectId : localSourceProjectId;
  const setSourceProjectId =
    typeof onSelectedProjectChange === "function" ? onSelectedProjectChange : setLocalSourceProjectId;

  const [targetProjectId, setTargetProjectId] = useState("");
  const [comparisonMode, setComparisonMode] = useState("manual");
  const [scan, setScan] = useState(null);
  const [history, setHistory] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  const [historyWarning, setHistoryWarning] = useState("");
  const [scanTime, setScanTime] = useState(null);
  const [view, setView] = useState("pending");
  const [grouping, setGrouping] = useState("file");
  const [search, setSearch] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setProjectsLoading(true);
      setProjectsError("");
      try {
        const nextProjects = await fetchReleaseSyncProjects();
        if (!active) return;
        setProjects(nextProjects);
      } catch (error) {
        if (active) setProjectsError(getErrorText(error));
      } finally {
        if (active) setProjectsLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (sourceProjectId || !projects.length) return;
    const defaultSource = projects.find((project) => isResolved(project));
    if (defaultSource?.project_id) setSourceProjectId(defaultSource.project_id);
  }, [projects, setSourceProjectId, sourceProjectId]);

  const sourceProject = useMemo(
    () => projects.find((project) => project.project_id === sourceProjectId) || null,
    [projects, sourceProjectId]
  );

  const eligibleTargets = useMemo(() => {
    if (!sourceProject?.application_id) return [];
    return projects.filter(
      (project) =>
        project.project_id !== sourceProject.project_id &&
        project.application_id === sourceProject.application_id &&
        isResolved(project) &&
        isProductionLike(project)
    );
  }, [projects, sourceProject]);

  useEffect(() => {
    if (comparisonMode !== "configured") return;
    if (!eligibleTargets.length) {
      if (targetProjectId) setTargetProjectId("");
      return;
    }
    if (!eligibleTargets.some((project) => project.project_id === targetProjectId)) {
      setTargetProjectId(eligibleTargets[0].project_id);
    }
  }, [comparisonMode, eligibleTargets, targetProjectId]);

  useEffect(() => {
    setScan(null);
    setHistory(null);
    setScanError("");
    setHistoryWarning("");
    setScanTime(null);
    setSelectedAsset(null);
  }, [sourceProjectId, targetProjectId]);

  const targetProject = useMemo(
    () => projects.find((project) => project.project_id === targetProjectId) || null,
    [projects, targetProjectId]
  );

  const sourceEligible = Boolean(sourceProject && isResolved(sourceProject) && isTestLike(sourceProject));
  const configuredPairEligible = Boolean(
    sourceEligible &&
      targetProject &&
      isResolved(targetProject) &&
      isProductionLike(targetProject) &&
      sourceProject.application_id &&
      sourceProject.application_id === targetProject.application_id
  );
  const manualPairEligible = Boolean(
    sourceProject &&
      targetProject &&
      isManualSelectable(sourceProject) &&
      isManualSelectable(targetProject) &&
      sourceProject.project_id !== targetProject.project_id
  );
  const pairEligible = comparisonMode === "manual" ? manualPairEligible : configuredPairEligible;

  const manualTargets = useMemo(
    () => projects.filter((project) => isManualSelectable(project) && project.project_id !== sourceProjectId),
    [projects, sourceProjectId]
  );

  const runScan = async () => {
    if (!pairEligible) {
      setScanError(
        comparisonMode === "manual"
          ? "Choose two different DEV IDE projects for the read-only comparison."
          : "Choose a resolved test/presandbox source and same-application sandbox/production target."
      );
      return;
    }

    setScanLoading(true);
    setScanError("");
    setHistoryWarning("");
    setScan(null);
    setHistory(null);
    try {
      const nextScan = await fetchReleaseSyncScan({
        sourceProjectId,
        targetProjectId,
        direction: DEFAULT_DIRECTION,
        includeTextDiffs: false,
        manualComparison: comparisonMode === "manual",
      });
      setScan(nextScan);
      setScanTime(new Date());

      try {
        const nextHistory = await fetchReleaseSyncHistory({
          sourceProjectId,
          targetProjectId,
          direction: DEFAULT_DIRECTION,
          includeAligned: false,
          manualComparison: comparisonMode === "manual",
        });
        setHistory({
          ...nextHistory,
          assets: (nextHistory.assets || []).map(compactWorkspaceHistoryAsset),
        });
      } catch (historyError) {
        setHistoryWarning(
          `Current hash scan completed, but Change/Full Change attribution could not be loaded: ${getErrorText(historyError)}`
        );
      }
    } catch (error) {
      setScanError(getErrorText(error));
    } finally {
      setScanLoading(false);
    }
  };

  const mergedAssets = useMemo(() => {
    const historyByPath = new Map((history?.assets || []).map((asset) => [asset.path, asset]));
    return (scan?.assets || []).map((asset) => {
      const historyAsset = historyByPath.get(asset.path);
      if (!historyAsset) return defaultReconciliation(asset);
      // Hash/baseline state is authoritative. Historical data explains provenance only.
      return { ...historyAsset, ...asset };
    });
  }, [scan, history]);

  const summary = useMemo(() => {
    const countState = (state) => mergedAssets.filter((asset) => asset.state === state).length;
    const targetAhead = countState("TARGET_AHEAD");
    const targetOnly = countState("TARGET_ONLY");
    return {
      currentParity: scan?.summary?.current_parity_total || 0,
      aligned: countState("ALIGNED"),
      sourceAhead: countState("SOURCE_AHEAD"),
      sourceNew: countState("SOURCE_NEW"),
      sourceDelete: countState("SOURCE_DELETE"),
      targetAhead,
      targetOnly,
      productionDrift: targetAhead + targetOnly,
      bothChangedSame: countState("BOTH_CHANGED_SAME"),
      conflict: countState("CONFLICT"),
      unknown: countState("UNTRACKED_UNKNOWN") + countState("UNREADABLE"),
      parityExcluded: mergedAssets.filter((asset) => asset.parity_excluded).length,
    };
  }, [mergedAssets, scan]);

  const releaseDifferenceSummary = useMemo(
    () => buildReleaseDifferenceSummary(mergedAssets, history?.release_delta_audit),
    [mergedAssets, history]
  );

  const baselineState = useMemo(() => {
    if (!scan) return "Not scanned";
    if (scan?.channel?.manual_comparison) {
      return "Manual read-only comparison; trusted baseline lifecycle does not apply to this explicit pair.";
    }
    if (scan?.baseline?.status === "UNAVAILABLE") {
      return `Baseline persistence unavailable: ${scan?.baseline?.persistence_error?.message || "unknown persistence error"}`;
    }
    const active = scan?.baseline?.active;
    if (active) {
      return `Trusted baseline ${active.snapshot_identity || `#${active.id}`} completed ${active.completed_at ? new Date(active.completed_at).toLocaleString() : "at an unknown time"}.`;
    }
    const blockers = scan?.baseline_eligibility?.blocking_asset_total || 0;
    if (!blockers) return "No trusted baseline yet; this fresh scan currently satisfies required parity.";
    return `No trusted baseline yet; ${blockers} required controlled asset(s) currently block baseline creation.`;
  }, [scan]);

  const filteredAssets = useMemo(() => {
    const needle = normalise(search);
    return mergedAssets.filter((asset) => {
      if (!matchesView(asset, view)) return false;
      if (!needle) return true;
      const relatedChanges = getAllSourceChanges(asset);
      const relatedFullChanges = getAllFullChanges(asset, relatedChanges);
      const changeText = relatedChanges.map((change) => `${change.change_id || ""} ${change.title || ""}`).join(" ");
      const fullChangeText = relatedFullChanges
        .map((fullChange) => `${fullChange.full_change_id || ""} ${fullChange.title || ""}`)
        .join(" ");
      return normalise(
        `${asset.path} ${asset.asset_type} ${asset.state} ${asset.provenance_status} ${changeText} ${fullChangeText}`
      ).includes(needle);
    });
  }, [mergedAssets, search, view]);

  const groupedAssets = useMemo(() => {
    const groups = new Map();
    filteredAssets.forEach((asset) => {
      const group = getGroup(asset, grouping);
      if (!groups.has(group.key)) groups.set(group.key, { ...group, assets: [] });
      groups.get(group.key).assets.push(asset);
    });
    return Array.from(groups.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [filteredAssets, grouping]);

  const channel = scan?.channel || history?.channel || null;
  const activeBaseline = scan?.baseline?.active || null;
  const baselineHistory = (scan?.baseline?.history || []).filter(
    (item) => String(item?.id ?? "") !== String(activeBaseline?.id ?? "")
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5 }, height: "100%", overflow: "auto" }}>
      <Stack spacing={1.5}>
        <Alert severity="info">
          <Typography variant="body2" fontWeight={700}>Release Sync — read-only phase</Typography>
          <Typography variant="body2">
            This workspace never copies, deletes, installs, promotes, rebuilds, restarts, or otherwise mutates either application environment.
            Run read-only scan uses the non-persisting scan path: it reads current hashes and baseline state without creating snapshots or other audit records.
          </Typography>
        </Alert>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Comparison configuration</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.25 }}>
            <Button
              size="small"
              variant={comparisonMode === "configured" ? "contained" : "outlined"}
              onClick={() => setComparisonMode("configured")}
            >
              Configured channel
            </Button>
            <Button
              size="small"
              variant={comparisonMode === "manual" ? "contained" : "outlined"}
              onClick={() => setComparisonMode("manual")}
            >
              Manual comparison
            </Button>
          </Stack>
          <Alert severity={comparisonMode === "manual" ? "warning" : "info"} sx={{ mb: 1.25, py: 0 }}>
            {comparisonMode === "manual"
              ? "Manual comparison lets you explicitly choose any two different DEV IDE projects by project id. It remains read-only and does not require or modify an official release channel."
              : "Configured channel uses the normal test/presandbox → sandbox/production relationship for the selected application."}
          </Alert>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 1fr) minmax(220px, 1fr) minmax(190px, 0.7fr) auto" },
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="release-sync-source-label">Source project</InputLabel>
              <Select
                labelId="release-sync-source-label"
                value={sourceProjectId || ""}
                label="Source project"
                onChange={(event) => setSourceProjectId(event.target.value)}
              >
                {projects.map((project) => (
                  <MenuItem
                    key={project.project_id}
                    value={project.project_id}
                    disabled={
                      comparisonMode === "configured"
                        ? !isResolved(project) || !isTestLike(project)
                        : !isManualSelectable(project)
                    }
                  >
                    {projectLabel(project)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth
              disabled={
                comparisonMode === "configured"
                  ? !sourceEligible || !eligibleTargets.length
                  : !sourceProjectId || !manualTargets.length
              }
            >
              <InputLabel id="release-sync-target-label">Target project</InputLabel>
              <Select
                labelId="release-sync-target-label"
                value={targetProjectId || ""}
                label="Target project"
                onChange={(event) => setTargetProjectId(event.target.value)}
              >
                {(comparisonMode === "manual" ? manualTargets : eligibleTargets).map((project) => (
                  <MenuItem key={project.project_id} value={project.project_id}>
                    {projectLabel(project)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Direction"
              value={comparisonMode === "manual" ? "Explicit source → target" : "Test → Production"}
              InputProps={{ readOnly: true }}
              helperText={comparisonMode === "manual" ? "Read-only manual selection" : "TEST_TO_PRODUCTION"}
            />

            <Button
              variant="contained"
              onClick={runScan}
              disabled={!pairEligible || scanLoading || projectsLoading}
              sx={{ minHeight: 40, whiteSpace: "nowrap" }}
            >
              {scanLoading ? "Scanning…" : "Run read-only scan"}
            </Button>
          </Box>

          {projectsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
              <CircularProgress size={18} />
              <Typography variant="caption">Loading project identities…</Typography>
            </Stack>
          ) : null}
          {projectsError ? <Alert severity="error" sx={{ mt: 1.25 }}>{projectsError}</Alert> : null}
          {!projectsLoading && comparisonMode === "configured" && sourceProject && !sourceEligible ? (
            <Alert severity="warning" sx={{ mt: 1.25 }}>
              The shared DEV IDE project remains selected, but configured Release Sync requires a resolved
              test/presandbox source. Use Manual comparison when stored environment metadata does not yet
              reflect the intended source/target role.
            </Alert>
          ) : null}
          {comparisonMode === "configured" && sourceEligible && !eligibleTargets.length ? (
            <Alert severity="warning" sx={{ mt: 1.25 }}>
              No resolved sandbox/production target for application {sourceProject?.application_id || "—"}
              is available in the active project list.
            </Alert>
          ) : null}
          {comparisonMode === "manual" && sourceProject && targetProject ? (
            <Alert severity="info" sx={{ mt: 1.25 }}>
              Comparing <strong>{projectLabel(sourceProject)}</strong> → <strong>{projectLabel(targetProject)}</strong>.
              Stored environment labels are shown for transparency; this manual choice does not rewrite them.
            </Alert>
          ) : null}
          {scanError ? <Alert severity="error" sx={{ mt: 1.25 }}>{scanError}</Alert> : null}
          {historyWarning ? <Alert severity="warning" sx={{ mt: 1.25 }}>{historyWarning}</Alert> : null}
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Release identity and baseline state</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: 1.25,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">Application</Typography>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {channel?.application_id || sourceProject?.application_id || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Release channel</Typography>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {channel?.channel_key || channel?.channel_identity || "Not resolved until scan"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Source identity</Typography>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {sourceProject?.project_identity || sourceProject?.project_id || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Target identity</Typography>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {targetProject?.project_identity || targetProject?.project_id || "—"}
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25 }}>
            Baseline state: {baselineState}
          </Typography>
          {activeBaseline ? (
            <Box sx={{ mt: 1.25, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>
                    Active trusted baseline {activeBaseline.snapshot_identity || `#${activeBaseline.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Source tree {shortHash(activeBaseline.source_tree_sha256)} • target tree {shortHash(activeBaseline.target_tree_sha256)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Explicit parity exclusions: {activeBaseline.parity_policy?.excluded_assets?.length || 0}
                  </Typography>
                </Box>
                <Chip size="small" color="success" label="Immutable baseline" />
              </Stack>
            </Box>
          ) : null}
          {baselineHistory.length ? (
            <Box sx={{ mt: 1.25 }}>
              <Typography variant="caption" fontWeight={700}>Historical baselines</Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {baselineHistory.slice(0, 5).map((item) => (
                  <Typography key={item.id} variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                    {item.snapshot_identity || `#${item.id}`} • {item.completed_at ? new Date(item.completed_at).toLocaleString() : "time unavailable"} • source {shortHash(item.source_tree_sha256)} • target {shortHash(item.target_tree_sha256)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}
          {scanTime ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              Last read-only scan: {scanTime.toLocaleString()} • no Release Sync snapshot recorded
            </Typography>
          ) : null}
        </Paper>

        {scan ? (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))", xl: "repeat(9, minmax(0, 1fr))" },
                gap: 1,
              }}
            >
              <SummaryCard label="Current parity" count={summary.currentParity} helper="Source and target equal now" />
              <SummaryCard label="Baseline aligned" count={summary.aligned} />
              <SummaryCard label="Source ahead" count={summary.sourceAhead} />
              <SummaryCard label="Source new" count={summary.sourceNew} />
              <SummaryCard label="Source delete" count={summary.sourceDelete} />
              <SummaryCard label="Production drift" count={summary.productionDrift} helper={`Target-ahead ${summary.targetAhead} • target-only ${summary.targetOnly}`} />
              <SummaryCard label="Both changed same" count={summary.bothChangedSame} helper="Current parity differs from baseline" />
              <SummaryCard label="Conflict" count={summary.conflict} helper="Both sides changed differently" />
              <SummaryCard label="Unknown / unreadable" count={summary.unknown} />
            </Box>

            <ReleaseDifferenceSummary data={releaseDifferenceSummary} onOpenAsset={setSelectedAsset} />

            <Paper variant="outlined">
              <Tabs
                value={view}
                onChange={(event, value) => setView(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Tab value="pending" label="Pending" />
                <Tab value="conflict" label="Conflict" />
                <Tab value="untracked" label="Untracked" />
                <Tab value="synced" label="Synced" />
                <Tab value="history" label="History" />
              </Tabs>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                sx={{ p: 1.25 }}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  size="small"
                  label="Filter files / Change / Full Change"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ flex: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
                  <InputLabel id="release-sync-group-label">Group by</InputLabel>
                  <Select
                    labelId="release-sync-group-label"
                    value={grouping}
                    label="Group by"
                    onChange={(event) => setGrouping(event.target.value)}
                  >
                    <MenuItem value="file">File</MenuItem>
                    <MenuItem value="change">DEV IDE Change</MenuItem>
                    <MenuItem value="fullChange">Full Change</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack spacing={1.25} sx={{ p: 1.25, pt: 0 }}>
                {groupedAssets.length ? (
                  groupedAssets.map((group) => (
                    <Box key={group.key}>
                      {grouping !== "file" ? (
                        <Typography variant="subtitle2" sx={{ mb: 0.75, overflowWrap: "anywhere" }}>
                          {group.label} ({group.assets.length})
                        </Typography>
                      ) : null}
                      <Stack spacing={0.75}>
                        {group.assets.map((asset) => (
                          <AssetRow key={asset.path} asset={asset} onOpen={setSelectedAsset} />
                        ))}
                      </Stack>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    No files match this Release Sync view and filter.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                Current tree hashes: source {shortHash(scan.source_tree_sha256)} • target {shortHash(scan.target_tree_sha256)}.
                {activeBaseline ? ` Active baseline: source ${shortHash(activeBaseline.source_tree_sha256)} • target ${shortHash(activeBaseline.target_tree_sha256)}.` : " No trusted baseline is active."}
                Historical Change/Full Change evidence explains provenance only and never overrides the hash-derived state.
              </Typography>
            </Paper>
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 2.5, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Choose any two different resolved projects in Manual comparison, or an eligible configured release pair, then run a read-only scan.
            </Typography>
          </Paper>
        )}
      </Stack>

      <ReleaseSyncFileHistoryDialog
        open={Boolean(selectedAsset)}
        onClose={() => setSelectedAsset(null)}
        asset={selectedAsset}
        sourceProjectId={sourceProjectId}
        targetProjectId={targetProjectId}
        direction={DEFAULT_DIRECTION}
        manualComparison={comparisonMode === "manual"}
      />
    </Box>
  );
};

export default ReleaseSyncPanel;
